import Big from 'big.js'
import { type Money } from './money'

/**
 * Distributes a total Money amount across items by weight using the
 * Largest Remainder Method, guaranteeing the results sum exactly to the total.
 *
 * Each item receives floor(exact_share) cents, then remaining cents are
 * distributed one-at-a-time to items with the largest fractional remainders.
 */
export function allocateProRata(total: Money, weights: number[], precision: number = 2): Money[] {
  const n = weights.length
  if (n === 0) return []

  const currency = total.currency
  const common_currency = total.common_currency || 'USD'
  const date = total.date

  const amounts = distributeField(total.amount, weights, precision)
  const commonAmounts = distributeField(total.common_amount || '0', weights, precision)

  return amounts.map((amount, i) => ({
    amount,
    common_amount: commonAmounts[i],
    currency,
    common_currency,
    date,
  }))
}

function distributeField(totalStr: string, weights: number[], precision: number): string[] {
  const n = weights.length
  const total = new Big(totalStr)
  const sumWeights = weights.reduce((s, w) => s + Number(w), 0)

  if (sumWeights === 0) {
    return weights.map(() => new Big(0).toFixed(precision))
  }

  const isNegative = total.lt(0)
  const absTotal = isNegative ? total.abs() : total

  // Compute exact shares and floor each
  const unit = new Big(10).pow(-precision) // e.g. 0.01 for precision=2
  const exact: Big[] = []
  const floored: Big[] = []
  const remainders: { index: number; remainder: Big }[] = []

  for (let i = 0; i < n; i++) {
    const share = absTotal.times(Number(weights[i])).div(sumWeights)
    exact.push(share)
    const f = floorTo(share, precision)
    floored.push(f)
    remainders.push({ index: i, remainder: share.minus(f) })
  }

  // How many units to distribute
  const flooredSum = floored.reduce((s, v) => s.plus(v), new Big(0))
  const shortfall = absTotal.minus(flooredSum)
  let unitsToDistribute = shortfall.div(unit).round(0, Big.roundHalfUp).toNumber()

  // Sort by remainder descending, then by original index for stable tiebreaker
  remainders.sort((a, b) => {
    const cmp = b.remainder.cmp(a.remainder)
    return cmp !== 0 ? cmp : a.index - b.index
  })

  for (const item of remainders) {
    if (unitsToDistribute <= 0) break
    floored[item.index] = floored[item.index].plus(unit)
    unitsToDistribute--
  }

  return floored.map((v) => {
    const result = isNegative ? v.times(-1) : v
    return result.toFixed(precision)
  })
}

function floorTo(value: Big, precision: number): Big {
  return value.round(precision, Big.roundDown)
}
